import React, { useState } from 'react';
import { ProductInfoRow } from './ProductDetailedInfoData';
import './styles/ProductDetailedInfo.css';
import './styles/ProductDetailedInfo.mobile.css';

type TabKey = 'description' | 'returnPolicy' | 'additionalDetails';

interface ProductData {
  _id: string;
  title: string;
  description?: string;
  shortDescription?: string;
  hsnCode?: string;
  variants?: any[];
  categories?: Array<{ name: string; slug: string }>;
  vendor?: string;
  brand?: { _id: string; name: string; slug: string };
  tags?: string[];
  packaging?: string;
}

interface ProductDetailedInfoProps {
  product?: ProductData;
  selectedVariant?: any;
}

const TAB_LABELS: Record<TabKey, string> = {
  description: 'Product Description',
  returnPolicy: 'Return & Exchange Policy',
  additionalDetails: 'Additional Details'
};

interface InfoTableProps {
  rows: ProductInfoRow[];
  headerTitle: string;
  isCollapsed: boolean;
  onToggle: () => void;
}

const InfoTable: React.FC<InfoTableProps> = ({ rows, isCollapsed }) => {
  return (
    <div className={`pdi-card ${isCollapsed ? 'pdi-collapsed' : ''}`}>
      {!isCollapsed && (
        <div className="pdi-table">
          {rows.map((row, idx) => (
            <div className="pdi-row" key={idx}>
              <div className="pdi-cell pdi-label">{row.label}</div>
              <div className="pdi-cell pdi-value">{row.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProductDetailedInfo: React.FC<ProductDetailedInfoProps> = ({ product, selectedVariant }) => {
  const [openSections, setOpenSections] = useState<Record<TabKey, boolean>>({
    description: false,
    returnPolicy: false,
    additionalDetails: false
  });
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const generateProductData = (): Record<TabKey, ProductInfoRow[]> => {
    if (!product) {
      return {
        description: [],
        returnPolicy: [],
        additionalDetails: []
      };
    }

    // Check if variants are color-based
    const hasColorVariants = product.variants?.some(variant => 
      variant.attributes && 
      Object.keys(variant.attributes).some(key => 
        key.toLowerCase().includes('color') || key.toLowerCase().includes('colour')
      )
    );

    // Get variant info for the selected variant only
    let variantInfo = 'N/A';
    if (selectedVariant) {
      if (hasColorVariants) {
        // For color variants, show the selected variant's title or color
        variantInfo = selectedVariant.variantTitle || selectedVariant.attributes?.color || selectedVariant.size || 'N/A';
      } else {
        // For non-color variants, show as packaging (selected variant only)
        const firstAttributeValue = selectedVariant.attributes ? Object.values(selectedVariant.attributes)[0] : selectedVariant.size;
        variantInfo = firstAttributeValue || 'N/A';
      }
    }

    const description: ProductInfoRow[] = [
      { label: 'Manufacture', value: product.brand?.name || product.vendor || 'N/A' },
      { label: 'Type', value: product.categories?.[0]?.name || 'N/A' },
      { 
        label: hasColorVariants ? 'Color' : 'Packaging', 
        value: hasColorVariants ? variantInfo : (product.packaging || variantInfo)
      },
      {
        label: 'Description',
        value: product.description ? (
          <div dangerouslySetInnerHTML={{ __html: product.description }} />
        ) : (
          product.shortDescription ? (
            <div dangerouslySetInnerHTML={{ __html: product.shortDescription }} />
          ) : 'No description available'
        )
      }
    ];

    const returnPolicy: ProductInfoRow[] = [
      { label: 'Return Window', value: 'Same day' },
      { label: 'Eligibility', value: 'Unused, in original packaging with all accessories' },
      { label: 'Process', value: 'Initiate request from orders page; pickup will be scheduled' },
      { label: 'Refund', value: 'Processed to original payment method after QA' }
    ];

    const additionalDetails: ProductInfoRow[] = [
      { label: 'Warranty', value: 'To be claimed from Manufacturer' },
      { label: 'Country of Origin', value: 'India' },
      { label: 'HSN Code', value: product.hsnCode || 'N/A' },
      { label: 'Tags', value: product.tags?.join(', ') || 'N/A' },
      { label: 'Care', value: 'Store in cool, dry place away from direct sunlight' }
    ];

    return {
      description,
      returnPolicy,
      additionalDetails
    };
  };

  const getRowsForTab = (key: TabKey): ProductInfoRow[] => {
    const productData = generateProductData();
    return productData[key];
  };

  if (isMobile) {
    // Mobile accordion layout
    return (
      <section className="product-detailed-info pdi-mobile-accordion">
        {(Object.keys(TAB_LABELS) as TabKey[]).map((key) => (
          <div className="pdi-accordion-section" key={key}>
            <button
              className={`pdi-accordion-header${openSections[key] ? ' open' : ''}`}
              onClick={() => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }))}
              aria-expanded={openSections[key]}
            >
              {TAB_LABELS[key]}
            </button>
            {openSections[key] && (
              <div className="pdi-accordion-content">
                <InfoTable
                  rows={getRowsForTab(key)}
                  headerTitle={TAB_LABELS[key]}
                  isCollapsed={false}
                  onToggle={() => {}}
                />
              </div>
            )}
          </div>
        ))}
      </section>
    );
  }
  // Desktop/tab layout
  return (
    <section className="product-detailed-info">
      <div className="pdi-tabs" role="tablist">
        {(Object.keys(TAB_LABELS) as TabKey[]).map((key) => (
          <button
            key={key}
            role="tab"
            aria-selected={openSections[key]}
            className={`pdi-tab ${openSections[key] ? 'active' : ''}`}
            onClick={() => setOpenSections({ description: false, returnPolicy: false, additionalDetails: false, [key]: true })}
          >
            {TAB_LABELS[key]}
          </button>
        ))}
      </div>
      <div className="pdi-content" role="tabpanel">
        <InfoTable
          rows={getRowsForTab((Object.keys(TAB_LABELS) as TabKey[]).find(k => openSections[k]) || 'description')}
          headerTitle={TAB_LABELS[(Object.keys(TAB_LABELS) as TabKey[]).find(k => openSections[k]) || 'description']}
          isCollapsed={false}
          onToggle={() => {}}
        />
      </div>
    </section>
  );
};

export default ProductDetailedInfo;


